/**
 * useShift Hook + ShiftProvider Context
 * CCH Care — Phase 2 + Outgoing Shift Mode (Phase 1)
 *
 * Path: src/hooks/useShift.js
 *
 * Changes (2026-06-26) — Server-authoritative grace minutes:
 * - Surfaces `graceMinutesRemaining` from the effective shift's
 *   `grace_minutes_remaining` field (computed FACILITY-side by the
 *   backend, same value GraceWindowBanner / the ADL flow-sheet banner
 *   read). Replaces AppShell's client-side recompute of grace-end from
 *   shift.end_time, which parsed a facility wall-clock time in the
 *   VIEWER's browser zone and so under-counted by the viewer↔facility
 *   offset (e.g. ~1h for a US-Eastern viewer of the Jamaica pilot).
 * - Null when not in grace / no shift. Cleared on logout.
 * - No backend change: current.php already emits grace_minutes_remaining
 *   on the shift object during the grace window.
 *
 * Changes (2026-06-16) — Outgoing incomplete-count:
 * - Adds outgoingIncompleteCount: the number of the outgoing shift's
 *   assigned residents not yet 9/9 complete (signed + N/A). Reuses
 *   residents/assigned.php pointed at the OUTGOING care_shift_id, so the
 *   count is computed by the exact same endpoint the dashboard renders —
 *   divergence-free by construction, and it includes residents with no
 *   record yet (that endpoint LEFT JOINs adl_records and counts them 0/9).
 * - "Incomplete" = adl_done < adl_total, NOT status === 'complete': the
 *   auto-complete regression (see pending-cosign.php) leaves 9/9 records
 *   as in_progress, so the status field would over-count incompletes.
 * - Count refreshes when the outgoing shift changes and after ADL actions
 *   (via refreshSummary). On fetch error it falls back to null, and the
 *   banner degrades to its generic message rather than losing the CTA.
 * - No backend change: current.php is untouched.
 *
 * Changes (2026-05-30) — Context refactor:
 * - All state and effects moved from the hook body into a new
 *   ShiftProvider so every consumer reads from one source of truth.
 *   Previously each component calling useShift() got its own useState
 *   instance, which meant the AppShell chip, ShiftDashboard, and
 *   ADLFlowSheetPage each had independent isOutgoingMode flags.
 *   Toggling mode in the AppShell chip did not propagate to the flow
 *   sheet's shift binding, so Sarah's documentation kept landing on
 *   her primary (Evening/PM) record even when she thought she was in
 *   outgoing Day mode. The 5/27 "ADL data bleed" and the 5/29 attempt
 *   to fix it via live useShift subscription both traced back to this
 *   — subscribing live to a per-component state instance that never
 *   actually toggles in that component.
 *
 * - ShiftProvider self-gates on authentication via useAuth — no fetch
 *   fires while the user is unauthenticated (so /login etc. don't 401).
 *   When the user authenticates, fetchShift's useCallback dep on the
 *   auth state triggers the initial fetch automatically. State is also
 *   cleared on logout to prevent stale data leaking across sessions.
 *
 * - useShift is now a thin useContext consumer. Return shape is
 *   identical to the previous hook — no other file needs changes.
 *
 * - Mount once near the app root, inside AuthProvider. See App.jsx:
 *     <AuthProvider>
 *       <ShiftProvider>
 *         <CareRouter />
 *       </ShiftProvider>
 *     </AuthProvider>
 *
 * Behavior preserved from earlier changelog entries:
 *
 * Changes (2026-05-27) — Outgoing-shift mode:
 * - Hook consumes the `outgoing_shift` field from current.php.
 * - Exposes primaryShift, outgoingShift, shift (effective),
 *   isOutgoingMode, enterOutgoingMode(), exitOutgoingMode().
 * - Mode change triggers a refetch via the fetchShift useCallback dep
 *   chain so summary and handoff notes re-load for the now-effective
 *   shift. Consumers reading `shift` see the swap transparently.
 * - Auto-exits outgoing mode when outgoingShift disappears (grace
 *   expired, another tab refreshed state, etc.).
 *
 * Changes (2026-05-13):
 * - Surface in_grace_window so the grace banner can branch correctly.
 * - When the active-shift countdown hits 0, refetch from server.
 * - Periodic 5-min refetch while inGraceWindow=true.
 *
 * Changes (2026-04-18):
 * - Expose minutes_until_start for the upcoming-shift banner.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from '../features/auth/useAuth';
import { getCurrentShift, getShiftSummary, getHandoffNotes } from '../api/shifts';
// Reused to compute the outgoing shift's incomplete-resident count — the
// same wrapper the resident hooks use; returns the parsed assigned.php body
// ({ residents: [...] }). NOTE arg order: (shiftAssignmentId, careShiftId).
import { getAssignedResidents } from '../api/patients';

const ShiftContext = createContext(null);

export function ShiftProvider({ children }) {
  // ── Auth gate ─────────────────────────────────────────────────
  // Skip all shift fetching for unauthenticated users. useAuth hydrates
  // `user` synchronously from localStorage on mount, so an already-
  // logged-in user sees the first fetch fire on the initial render.
  // When `user` flips (login or logout), fetchShift's useCallback dep
  // recreates the function, which triggers the bootstrap useEffect.
  const { user } = useAuth();
  const isAuthenticated = !!user;

  // ── Shift data from server ────────────────────────────────────
  const [primaryShift,   setPrimaryShift]   = useState(null);
  const [outgoingShift,  setOutgoingShift]  = useState(null);
  const [isOutgoingMode, setIsOutgoingMode] = useState(false);

  const [shiftState,         setShiftState]         = useState('loading');
  const [inGraceWindow,      setInGraceWindow]      = useState(false);
  // Server-authoritative minutes left in the late-doc grace window,
  // computed in the FACILITY timezone by the backend. Null outside grace.
  // Do NOT recompute this client-side from end_time — that re-parses a
  // facility wall-clock value in the viewer's browser zone (the bug this
  // field replaces). See AppShell formatGrace().
  const [graceMinutesRemaining, setGraceMinutesRemaining] = useState(null);
  const [summary,            setSummary]            = useState(null);
  const [handoffNotes,       setHandoffNotes]       = useState([]);
  const [accessibleUnits,    setAccessibleUnits]    = useState([]);
  const [minutesRemaining,   setMinutesRemaining]   = useState(null);
  const [minutesUntilStart,  setMinutesUntilStart]  = useState(null);
  const [loading,            setLoading]            = useState(true);
  const [error,              setError]              = useState(null);

  // Outgoing shift's incomplete-resident count (null = unknown / no
  // outgoing shift; the banner treats null as "fall back to generic text").
  const [outgoingIncompleteCount, setOutgoingIncompleteCount] = useState(null);

  const timerRef         = useRef(null);
  const upcomingTimerRef = useRef(null);

  // Effective shift — what consumers should treat as "the current shift."
  // Falls back to primary if outgoing is requested but unavailable.
  const shift = (isOutgoingMode && outgoingShift) ? outgoingShift : primaryShift;

  // ── Fetch + sync ──────────────────────────────────────────────
  const fetchShift = useCallback(async () => {
    if (!isAuthenticated) {
      // No-op for unauthenticated routes. State clears via the
      // dedicated logout effect below; this just prevents 401s.
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await getCurrentShift();

      if (!data.success || !data.shift) {
        setPrimaryShift(null);
        setOutgoingShift(null);
        setIsOutgoingMode(false);
        setShiftState(data.state || 'no_shift');
        setInGraceWindow(false);
        setGraceMinutesRemaining(null);
        setMinutesRemaining(null);
        setMinutesUntilStart(null);
        setLoading(false);
        return;
      }

      setPrimaryShift(data.shift);
      setOutgoingShift(data.outgoing_shift || null);
      setShiftState(data.state);
      setAccessibleUnits(data.accessible_units || []);
      setMinutesUntilStart(data.minutes_until_start ?? null);

      // Effective shift for summary/notes — based on current mode.
      // If we're in outgoing mode but outgoing came back null, fall
      // back to primary.
      const effective = (isOutgoingMode && data.outgoing_shift)
        ? data.outgoing_shift
        : data.shift;

      setInGraceWindow(!!effective.in_grace_window);
      // Facility-frame grace minutes straight off the effective shift.
      // Covers both the current-shift grace case and outgoing mode (the
      // outgoing shift object carries the same field). ?? null so a
      // missing field reads as "no grace value" rather than undefined.
      setGraceMinutesRemaining(effective.grace_minutes_remaining ?? null);
      setMinutesRemaining(effective.minutes_remaining);

      // Summary + handoff notes for the effective shift only.
      const [summaryData, notesData] = await Promise.allSettled([
        getShiftSummary(effective.care_shift_id, effective.shift_assignment_id),
        getHandoffNotes(effective.care_shift_id),
      ]);

      if (summaryData.status === 'fulfilled' && summaryData.value.success) {
        setSummary(summaryData.value.summary);
      }
      if (notesData.status === 'fulfilled' && notesData.value.success) {
        setHandoffNotes(notesData.value.handoff_notes || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load shift');
      setShiftState('error');
    } finally {
      setLoading(false);
    }
  }, [isOutgoingMode, isAuthenticated]);

  // ── Outgoing incomplete-resident count ────────────────────────
  // Reuses residents/assigned.php pointed at the outgoing shift so the
  // count matches the dashboard exactly. "Incomplete" = adl_done <
  // adl_total (status-bug-proof; see file header). Null on no-outgoing
  // or error so the banner degrades to its generic message.
  const refreshOutgoingCount = useCallback(async () => {
    if (!outgoingShift?.care_shift_id) {
      setOutgoingIncompleteCount(null);
      return;
    }
    try {
      const data = await getAssignedResidents(
        outgoingShift.shift_assignment_id ?? null,
        outgoingShift.care_shift_id,
      );
      const residents = data?.residents || [];
      const incomplete = residents.filter(
        (r) => (r.adl_done ?? 0) < (r.adl_total ?? 9),
      ).length;
      setOutgoingIncompleteCount(incomplete);
    } catch {
      setOutgoingIncompleteCount(null);
    }
  }, [outgoingShift]);

  // Refresh the count whenever the outgoing shift appears/changes.
  useEffect(() => {
    refreshOutgoingCount();
  }, [refreshOutgoingCount]);

  // ── Logout cleanup ────────────────────────────────────────────
  // Reset state when the user logs out so stale shift data doesn't
  // leak into a subsequent login. The fetchShift early-return above
  // already prevents new fetches; this clears what was previously
  // loaded. Doesn't run on mount unless the user starts unauthenticated.
  useEffect(() => {
    if (!isAuthenticated) {
      setPrimaryShift(null);
      setOutgoingShift(null);
      setIsOutgoingMode(false);
      setShiftState('loading');
      setInGraceWindow(false);
      setGraceMinutesRemaining(null);
      setSummary(null);
      setHandoffNotes([]);
      setAccessibleUnits([]);
      setMinutesRemaining(null);
      setMinutesUntilStart(null);
      setOutgoingIncompleteCount(null);
      setError(null);
    }
  }, [isAuthenticated]);

  // ── Auto-exit outgoing mode if the outgoing shift disappears ──
  // Happens when grace expires, or another tab refreshed state.
  // Without this, isOutgoingMode could remain stuck true even though
  // the effective shift has silently fallen back to primary.
  useEffect(() => {
    if (isOutgoingMode && !outgoingShift) {
      setIsOutgoingMode(false);
    }
  }, [isOutgoingMode, outgoingShift]);

  // ── Active-shift countdown ────────────────────────────────────
  // When the countdown hits 0, refetch from server. The server keeps
  // state='active' during the grace window, so we don't unilaterally
  // flip to 'ended' here — that would prematurely lock CNAs out.
  useEffect(() => {
    if (shiftState !== 'active' || minutesRemaining === null) return;

    timerRef.current = setInterval(() => {
      setMinutesRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timerRef.current);
          fetchShift();
          return 0;
        }
        return prev - 1;
      });
    }, 60000);

    return () => clearInterval(timerRef.current);
  }, [shiftState, minutesRemaining, fetchShift]);

  // ── Upcoming-shift countdown ──────────────────────────────────
  useEffect(() => {
    if (shiftState !== 'upcoming' || minutesUntilStart === null) return;

    upcomingTimerRef.current = setInterval(() => {
      setMinutesUntilStart(prev => {
        if (prev === null || prev <= 0) {
          clearInterval(upcomingTimerRef.current);
          fetchShift();
          return 0;
        }
        return prev - 1;
      });
    }, 60000);

    return () => clearInterval(upcomingTimerRef.current);
  }, [shiftState, minutesUntilStart, fetchShift]);

  // ── Grace-window poll — every 5 min while in grace ────────────
  // Each poll refreshes graceMinutesRemaining from the server, so the
  // header pill ticks down at 5-min granularity with the facility-frame
  // value (no client-side time math, no viewer-zone drift).
  useEffect(() => {
    if (!inGraceWindow) return;
    const id = setInterval(fetchShift, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [inGraceWindow, fetchShift]);

  // ── Refresh summary (called after ADL actions) ────────────────
  const refreshSummary = useCallback(async () => {
    if (!shift) return;
    try {
      const data = await getShiftSummary(shift.care_shift_id, shift.shift_assignment_id);
      if (data.success) setSummary(data.summary);
    } catch (err) {
      console.error('Failed to refresh summary:', err);
    }
    // Keep the outgoing incomplete count fresh after ADL actions so the
    // banner count drops as the CNA finishes their ending shift.
    refreshOutgoingCount();
  }, [shift, refreshOutgoingCount]);

  // Auto-refresh summary every 5 minutes
  useEffect(() => {
    if (shiftState !== 'active' || !shift) return;
    const interval = setInterval(refreshSummary, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [shiftState, shift, refreshSummary]);

  // Initial fetch — also fires whenever isOutgoingMode or
  // isAuthenticated toggles (fetchShift's useCallback deps flow
  // through here).
  useEffect(() => {
    fetchShift();
  }, [fetchShift]);

  // Mode-swap handlers — entering only succeeds if an outgoing shift
  // exists. Now that state is shared via context, calling these from
  // any consumer (AppShell chip, OutgoingShiftBanner on the dashboard,
  // etc.) updates the single source of truth and every consumer reacts.
  const enterOutgoingMode = useCallback(() => {
    if (outgoingShift) setIsOutgoingMode(true);
  }, [outgoingShift]);

  const exitOutgoingMode = useCallback(() => {
    setIsOutgoingMode(false);
  }, []);

  // Format time remaining as H:MM
  const timeDisplay = minutesRemaining !== null
    ? `${Math.floor(minutesRemaining / 60)}:${String(minutesRemaining % 60).padStart(2, '0')}`
    : '--:--';

  const value = {
    shift,                  // effective: primary, OR outgoing when in outgoing mode
    primaryShift,           // always primary
    outgoingShift,          // always outgoing (or null)
    outgoingIncompleteCount,// residents on the outgoing shift not yet 9/9 (null = unknown)
    isOutgoingMode,
    enterOutgoingMode,
    exitOutgoingMode,

    shiftState,
    inGraceWindow,
    graceMinutesRemaining,  // facility-frame minutes left in grace (null outside grace)
    summary,
    handoffNotes,
    accessibleUnits,
    minutesRemaining,
    minutesUntilStart,
    timeDisplay,
    loading,
    error,
    refreshShift: fetchShift,
    refreshSummary,
    refreshOutgoingCount,
  };

  return <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>;
}

/**
 * useShift — read shared shift state from context.
 *
 * Throws if used outside of ShiftProvider so misplaced consumers fail
 * loud rather than silently rendering wrong data. If you hit this in a
 * route that should be public, the route probably shouldn't be calling
 * useShift; if it's an authenticated route, ShiftProvider isn't mounted
 * above it.
 */
export default function useShift() {
  const ctx = useContext(ShiftContext);
  if (!ctx) {
    throw new Error('useShift must be used within a ShiftProvider');
  }
  return ctx;
}